import React from "react";
import { motion } from "framer-motion";

interface PasswordStrengthBarProps {
  password: string;
}

function getStrength(password: string): { score: number; label: string; color: string } {
  if (!password) return { score: 0, label: "", color: "" };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: "Weak", color: "rgb(var(--danger))" };
  if (score === 2) return { score: 2, label: "Fair", color: "rgb(var(--warning))" };
  if (score === 3) return { score: 3, label: "Good", color: "rgb(var(--info))" };
  return { score: 4, label: "Strong", color: "rgb(var(--success))" };
}

export default function PasswordStrengthBar({ password }: PasswordStrengthBarProps) {
  if (!password) return null;

  const { score, label, color } = getStrength(password);

  return (
    <div className="flex flex-col gap-1.5 mt-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className="h-1 flex-1 rounded-full"
            animate={{ backgroundColor: i <= score ? color : "rgb(var(--surface-3))" }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          />
        ))}
      </div>
      <motion.span
        key={label}
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.25 }}
        className="text-[10px] font-medium"
        style={{ color }}
      >
        {label}
      </motion.span>
    </div>
  );
}
